/**
 * Звуковая обратная связь (И7, Часть 4) — Web Audio API inline.
 *
 * 0 внешних зависимостей и 0 аудио-файлов в репо: все звуки синтезируются на лету
 * короткими осцилляторами (tone/аркосцы/аккорды). Это укладывается в лимит RAM
 * (~967 МБ): AudioContext создаётся лениво и один, ноты — короткие (~десятки мс).
 *
 * Политика autoplay: браузеры требуют жест пользователя для запуска звука. Поэтому
 * AudioContext создаётся/резюмируется при первом взаимодействии (unlockAudio),
 * а playSound дополнительно резюмит suspended-контекст. До жеста звук тихо
 * пропускается (никаких ошибок).
 *
 * Mute хранится в localStorage (ключ kikayoo:sound): 'off' = выключено, иначе вкл.
 *
 * Тестирование: Web Audio тяжело проверять в jsdom (нет реального AudioContext),
 * поэтому модуль спроектирован no-op-безопасным вне браузера/при mute и
 * проверяется вручную (см. отчёт И7). Тайп-гарды typeof исключают падения в тестах.
 */

const SOUND_KEY = 'kikayoo:sound';

/** Имена звуковых событий (для логов/тестов). */
export const SoundEvent = {
  Click: 'click',
  Tick: 'tick',
  Correct: 'correct',
  Wrong: 'wrong',
  Reveal: 'reveal',
  Finish: 'finish',
} as const;

export type SoundEventName = (typeof SoundEvent)[keyof typeof SoundEvent];

let ctx: AudioContext | null = null;

/** Окно с опциональным webkit-префиксом AudioContext (старые Safari). */
interface WindowWithWebkit {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

/** Возвращает (и при первом обращении создаёт) AudioContext, либо null если недоступен. */
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as WindowWithWebkit;
  const AC = w.AudioContext ?? w.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    try {
      ctx = new AC();
    } catch {
      ctx = null;
    }
  }
  return ctx;
}

/**
 * unlockAudio создаёт/возобновляет AudioContext. Вызывать из обработчика жеста
 * пользователя (клик/тап) — иначе браузер держит контекст в 'suspended' и звуки
 * не звучат (autoplay policy). Безопасно вызывать многократно.
 */
export function unlockAudio(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') {
    void c.resume().catch(() => {
      /*ignore*/
    });
  }
}

/** Включён ли звук (default = вкл, если в localStorage ничего не задано). */
export function isSoundEnabled(): boolean {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(SOUND_KEY);
    return v === null ? true : v !== 'off';
  } catch {
    return false;
  }
}

/** Включает/выключает звук и сохраняет выбор в localStorage. */
export function setSoundEnabled(on: boolean): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  try {
    window.localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* квота/приватный режим — игнорируем */
  }
}

/**
 * Проигрывает один тон: осциллятор с ADSR-подобной огибающей громкости
 * (быстрая атака + экспоненциальный спад). Подключается к destination напрямую.
 */
function tone(
  freq: number,
  startOffset: number,
  dur: number,
  type: OscillatorType,
  gainPeak: number,
): void {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = c.currentTime + startOffset;
  // Атака ~10 мс, затем экспоненциальный спад до тишины (избегаем щелчков).
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(gainPeak, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/**
 * Проигрывает звуковой эффект по имени. No-op при mute, вне браузера или без
 * AudioContext. Дополнительно резюмит suspended-контекст (жест уже был — иначе
 * resume всё равно не сработает и звук тихо пропустится).
 */
export function playSound(event: SoundEventName): void {
  if (!isSoundEnabled()) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    void c.resume().catch(() => {
      /*ignore*/
    });
  }
  switch (event) {
    case 'click':
      tone(520, 0, 0.08, 'sine', 0.12);
      break;
    case 'tick':
      tone(880, 0, 0.05, 'square', 0.06);
      break;
    case 'correct':
      tone(660, 0, 0.12, 'sine', 0.14);
      tone(990, 0.1, 0.18, 'sine', 0.14);
      break;
    case 'wrong':
      tone(200, 0, 0.22, 'sawtooth', 0.12);
      break;
    case 'reveal':
      tone(523, 0, 0.1, 'triangle', 0.12);
      tone(784, 0.09, 0.16, 'triangle', 0.12);
      break;
    case 'finish':
      // короткое восходящее арпеджио (до-ми-соль-до)
      tone(523, 0, 0.15, 'triangle', 0.13);
      tone(659, 0.13, 0.15, 'triangle', 0.13);
      tone(784, 0.26, 0.15, 'triangle', 0.13);
      tone(1047, 0.39, 0.28, 'triangle', 0.15);
      break;
    default:
      break;
  }
}

// Удобные точечные обёртки (читаемее в местах вызова: playClick() вместо playSound('click')).
export const playClick = (): void => playSound(SoundEvent.Click);
export const playTick = (): void => playSound(SoundEvent.Tick);
export const playCorrect = (): void => playSound(SoundEvent.Correct);
export const playWrong = (): void => playSound(SoundEvent.Wrong);
export const playReveal = (): void => playSound(SoundEvent.Reveal);
export const playFinish = (): void => playSound(SoundEvent.Finish);
